import RenderManager from "../rendering/render-manager";
import getVWKey from "../helpers/getVWKey";
import Utils from "../utils";
import { meshRaycaster } from "../interactivity/add-interactivity";
import Network from "../miniviews/network";
import { fire } from "../utils/events";
import EntranceExit from "./EntranceExit";
import Base2DElement from "./base-2d-element";
import * as Events from "../utils/events";

const g_origin = new THREE.Vector2();
const g_bounds = new Array(4).fill(1).map(() => new THREE.Vector2());
const g_pos1 = new THREE.Vector2();
const g_pos2 = new THREE.Vector2();
const g_mid = new THREE.Vector2();
const g_dir = new THREE.Vector2();
const g_off1 = new THREE.Vector2();
const g_off2 = new THREE.Vector2();

const EndpointMarkers = {
    LINE: 'line',
    SINGLE_ARROW: 'single_arrow',
    DOUBLE_ARROWS: 'double_arrows',
    BRACKET: 'bracket',
};

function convertTargets(options, data) {
    let targets = [
        {
            type: null,
            uuid: "auto",
            point: false,
        },
        {
            type: null,
            uuid: "auto",
            point: false,
        }
    ];
    if (options.textBoxUuid) {
        targets[0] = {
            type: "2D",
            uuid: options.textBoxUuid,
            point: null,
        }
    } else if (options.targetUuid1 && options.targetPoint1) {
        targets[1] = {
            type: "3D",
            uuid: options.targetUuid1,
            point: options.targetPoint1,
        }
    }
    if (options.targetUuid && options.targetPoint) {
        targets[1] = {
            type: "3D",
            uuid: options.targetUuid,
            point: options.targetPoint,
        }
    }
    data.targets = targets;

    ["targetUuid", "targetPoint", "targetUuid1", "targetPoint1", "textBoxUuid"].forEach(keyToDel => {delete data[keyToDel]});

    return targets;
}

class LineTarget {
    constructor(targetData) {
        this.type = targetData.type;
        this.uuid = targetData.uuid;
        this.point = targetData.point ? new THREE.Vector3().copy(targetData.point) : null;
    }

    get object() {
        return this.type === "3D" ? this.obj3D : this.obj2D;
    }

    get obj3D() {
        return this.type === "3D" && this.uuid ? Q3.assets.objects[this.uuid] : null;
    }

    get obj2Ddata() {
        let lData = Q3.data.layers.layers2D[this.uuid]
        if (lData && lData.objects && Object.values(lData.objects)[0]) {
            return Object.values(lData.objects)[0]
        } else {
            return null;
        }
    }

    get obj2D() {
        if (this.uuid && Q3.layers.list[this.uuid] && Q3.layers.list[this.uuid].objects[0]) {
            return Q3.layers.list[this.uuid].objects[0];
        } else {
            return null;
        }
    }
}
class Line extends Base2DElement{

    get data() { return Q3.data.layers.layers2D[this.parentLayer.uuid].objects[this.uuid]; }

    get inLetterbox() {
        return !this.targets.filter(t => t.uuid && (t.type === "3D" || t.obj2Ddata && t.obj2Ddata.inLetterbox === false)).length;
    }

    constructor(options, parentLayer, uuid = THREE.Math.generateUUID()) {
        super(options, parentLayer, uuid);

        let initialData = {
            x1: options.x1,
            x2: options.x2,
            y1: options.y1,
            y2: options.y2,
        };

        this.sizeSm = Object.assign({}, options.sizeSm && Object.keys(options.sizeSm).length ? options.sizeSm : initialData);
        this.sizeLg = Object.assign({}, options.sizeLg || initialData);
        if (!options.sizeLg) {
            this.data.sizeLg = initialData;
        }

        if (this.arrow === false) {
            this.arrow = EndpointMarkers.LINE;
        } else if (this.arrow === true) {
            this.arrow = EndpointMarkers.SINGLE_ARROW;
        }

        this.type2dAnimation = [];

        this.vector0 = new THREE.Vector3();
        this.vector1 = new THREE.Vector3();

        this.domElement = document.createElementNS(Base2DElement.Namespace, 'g');
        this.animElement = document.createElementNS(Base2DElement.Namespace, 'g');
        this.lineElement = document.createElementNS(Base2DElement.Namespace, 'path');
        this.outlineElement = document.createElementNS(Base2DElement.Namespace, 'path');
        this.hiddenPath = document.createElementNS(Base2DElement.Namespace, 'path');

        this.hiddenPath.style.opacity = 0;
        this.hiddenPath.setAttribute('stroke-linecap', "round");
        this.hiddenPath.setAttribute('stroke', "white");
        this.hiddenPath.setAttribute('stroke-width', 20);

        this.lineElement.setAttribute('stroke', this.color);
        this.outlineElement.setAttribute('stroke', this.outlineColor);
        this.lineElement.setAttribute('stroke-linecap', "round");
        this.outlineElement.setAttribute('stroke-linecap', "round");

        this.lineElement.classList.add('q3-line');
        this.outlineElement.classList.add('q3-line');
        this.hiddenPath.classList.add('q3-line-editor');
        this.domElement.id = this.uuid;
        this.parentLayer.domElement.appendChild(this.domElement);
        this.domElement.appendChild(this.animElement);
        this.animElement.appendChild(this.hiddenPath);
        this.animElement.appendChild(this.outlineElement);
        this.animElement.appendChild(this.lineElement);

        if (Q3.editMode) { //drag line
            this.enterExitCtrl = new EntranceExit(this, [this.animElement]);

            this.domElement.setAttribute("type-2d", this.type2d);
            this.domElement.addEventListener("mouseover", () => {
                this.domElement.classList.add("mouseover-2d");
            });
            this.domElement.addEventListener("mouseout", () => {
                this.domElement.classList.remove("mouseover-2d");
            })
            let getNewVal = function (val, delta, size) {
                return val * size - delta;
            }

            let setPath = (linePath) => {
                this.lineElement.setAttribute('d', linePath);
                this.outlineElement.setAttribute('d', linePath);
                this.hiddenPath.setAttribute('d', linePath);
            }

            let onMouseMove = (e) => {
                if (Q3.data.layers.layers2D[parentLayer.uuid].objects[this.uuid].lock) {
                    return;
                }

                let { width: w, height: h } = RenderManager.getSize();

                this.isMoving = true;

                let deltaX = this.mousedownCoord.x - e.x;
                let deltaY = this.mousedownCoord.y - e.y;
                let x1 = getNewVal(this.x1, deltaX, w);
                let x2 = getNewVal(this.x2, deltaX, w);
                let y1 = getNewVal(this.y1, deltaY, h);
                let y2 = getNewVal(this.y2, deltaY, h);

                if (x1 <= 0) {
                    x1 = 0;
                    x2 = this.move.x2 - this.move.x1;
                }

                if (x2 <= 0) { x2 = 0; x1 = this.move.x1 - this.move.x2; }
                if (y1 <= 0) { y1 = 0; y2 = this.move.y2 - this.move.y1; }
                if (y2 <= 0) { y2 = 0; y1 = this.move.y1 - this.move.y2; }
                if (x1 >= w) { x1 = w; x2 = this.move.x2 + (w - this.move.x1); }
                if (x2 >= w) { x2 = w; x1 = this.move.x1 + (w - this.move.x2); }
                if (y1 >= h) { y1 = h; y2 = this.move.y2 + (h - this.move.y1); }
                if (y2 >= h) { y2 = h; y1 = this.move.y1 + (h - this.move.y2); }

                this._setPoints(x1, y1, x2, y2, this.thickness * w / 1000);
                this.move = { x1: x1, x2: x2, y1: y1, y2: y2 };
            };

            let onMouseUp = (e) => {
                this.isDragging = false;

                document.body.classList.remove('is-dragging-line');

                window.removeEventListener('mousemove', onMouseMove);
                window.removeEventListener('mouseup', onMouseUp);

                if (this.isMoving) {
                    const { width, height } = RenderManager.getSize();
                    let newCoords = {
                        x1: this.move.x1 / width,
                        x2: this.move.x2 / width,
                        y1: this.move.y1 / (height),
                        y2: this.move.y2 / (height),
                    };

                    Object.assign(this, newCoords);

                    let currentSize = this.getVWSize();

                    if (!this.data[currentSize]) {
                        this.data[currentSize] = {};
                    }
                    Object.assign(this.data[currentSize], newCoords);

                    this.isMoving = false;

                    fire("line_changed");
                } else {
                    fire("line_not_changed");
                }
            }

            let mouseDownHandler = (e) => {
                if (this.targets.filter(t => t.obj2D !== null).length) return;

                fire("line_change_start", this);

                let w = RenderManager.domElement.width;
                let h = RenderManager.domElement.height;

                this.move = { x1: this.x1 * w, x2: this.x2 * w, y1: this.y1 * h, y2: this.y2 * h };
                this.isDragging = true;

                document.body.classList.add('is-dragging-line');

                this.mousedownCoord = { x: e.x, y: e.y };

                window.addEventListener('mousemove', onMouseMove);
                window.addEventListener('mouseup', onMouseUp);
            }

            this.lineElement.addEventListener('mousedown', mouseDownHandler);
            this.outlineElement.addEventListener('mousedown', mouseDownHandler);
            this.hiddenPath.addEventListener('mousedown', mouseDownHandler);
            this.enterExitCtrl.init({ animation: options.animation, leavingAnimation: options.leavingAnimation });
        }

        let onClick = () => {
            if (Q3.editMode) {
                if (Q3.isSelecting2dObject || Q3.assigningLine) return;
                if (this.preventSelect) {
                    delete this.preventSelect;
                    return;
                }
                Q3.selected = this;
                fire('selectchange');
                RenderManager.activeCamera.update = true;
            }
        };

        this.lineElement.addEventListener('click', onClick, false);
        this.outlineElement.addEventListener('click', onClick, false);
        this.hiddenPath.addEventListener('click', onClick, false);

        if (Q3.editMode) {
            this.lineElement.classList.add('q3-line-editor');
            this.outlineElement.classList.add('q3-line-editor');
        }

        const { width, height } = RenderManager.getSize();

        this.render(width, height);
        this.frameAnimation = this.frameAnimation.bind(this);
        this.frameAnimation()
    }

    initEnterExit() {
        const options = this.options;
        this.type2dAnimationLeaving = [];
        this.type2dAnimation = this.targets.filter(t => t.type === "2D" && t.obj2Ddata).reduce((result, t) => {
            let obj = t.obj2Ddata;
            if (!!obj.animation && obj.animation.name !== '') {
                result.push(obj);
                if (!!obj.leavingAnimation && obj.leavingAnimation.name !== '') this.type2dAnimationLeaving.push(obj)
            }
            return result
        }, []);
        // set Animation for Line
        let enterance = null;
        let exit = null;
        if (options.animation && options.animation.name) {
            enterance = options.animation;
        }
        if (options.leavingAnimation && options.leavingAnimation.name) {
            exit = options.leavingAnimation;
        }
        if (this.type2dAnimation.length === 2) {
            let maxEnter = Math.max(...this.type2dAnimation.map(o => o.animation.delay));
            enterance = enterance || { name: 'fadeIn', delay: maxEnter, duration: 0.5 };
            if (this.type2dAnimationLeaving.length === 2) {
                const getFullTime = (o) => {
                    return o.animation.duration + o.animation.delay + o.leavingAnimation.duration + o.leavingAnimation.delay
                };
                let firstExit = this.type2dAnimationLeaving.sort((o1, o2) => {
                    return getFullTime(o1) <= getFullTime(o2) ? -1 : 1;
                })[0];
                let exitDelay = getFullTime(firstExit) - firstExit.leavingAnimation.delay - 0.5;
                if (exitDelay && this.type2dAnimationLeaving.length) {
                    exit = exit || { name: 'fadeOut', delay: exitDelay, duration: 0.5 }
                }
            }
        }
        if (this.type2dAnimation.length === 1) {
            let objEnter = this.type2dAnimation[0].animation;
            enterance = enterance || { name: 'fadeIn', delay: objEnter.delay, duration: objEnter.duration }
        }

        if (this.type2dAnimationLeaving.length === 1) {
            let objLeave = this.type2dAnimationLeaving[0].leavingAnimation;
            exit = exit || { name: 'fadeOut', delay: objLeave.delay, duration: objLeave.duration }
        }
        this.enterExitCtrl = new EntranceExit(this, [this.animElement]);
        this.enterExitCtrl.init({ animation: enterance, leavingAnimation: exit });
    }

    onReadyToAnimate() {
        if (Q3.editMode) return;
        this.tourIsReady().then(() => {
            if (!this.enterExitCtrl) {
                this.initEnterExit();
            }
        });
    }

    _initValues(options) {
        this.type = '2DObject';
        this.type2d = "Line";
        this.index = options.index;
        this.color = options.color;
        this.outlineColor = options.outlineColor || "#000";
        this.lineType = options.lineType || "solid"
        this.thickness = options.thickness;
        this.arrow = options.arrow;

        this._targets = (options.targets ? options.targets : convertTargets(options, this.data)).map(t => new LineTarget(t));
    }

    frameAnimation() {
        if (!this.type2dAnimation.length) return;
        window.requestAnimationFrame(this.frameAnimation);
        // this.redraw();
    }
    tourIsReady() {
        return new Promise((resolve) => {
            if (Q3.isReady) {
                resolve();
            } else {
                Events.listen("tourReady", () => {
                    resolve();
                });
            }
        })
    }

    _setCoordinate(coord, val) {
        let currentSize = this.getVWSize();
        this[currentSize][coord] = val;
    }

    _getCoordinate(coord) {
        let currentSize = this.getVWSize();
        return this[currentSize][coord];
    }

    set x1(val) { this._setCoordinate('x1', val); }
    set x2(val) { this._setCoordinate('x2', val); }
    set y1(val) { this._setCoordinate('y1', val); }
    set y2(val) { this._setCoordinate('y2', val); }
    get x1() { return this._getCoordinate('x1'); }
    get x2() { return this._getCoordinate('x2'); }
    get y1() { return this._getCoordinate('y1'); }
    get y2() { return this._getCoordinate('y2'); }
    get maxX() { return this.x1 >= this.x2 ? "x1" : "x2" }
    get maxY() { return this.y1 >= this.y2 ? "y1" : "y2" }
    get minX() { return this.x1 <= this.x2 ? "x1" : "x2" }
    get minY() { return this.y1 <= this.y2 ? "y1" : "y2" }
    get width() { return this[this.maxX] - this[this.minX] } // box width
    get height() { return this[this.maxY] - this[this.minY] } // box width
    get x() { return this[this.minX] };
    get y() { return this[this.minY] };

    setData(data) {
        let minX = this.minX;
        let minY = this.minY;
        let maxX = this.maxX;
        let maxY = this.maxY;
        let w = this.width;
        let h = this.height;
        this[minX] = data.x;
        this[maxX] = data.x + w;
        this[minY] = data.y;
        this[maxY] = data.y + h;
        let currentSize = this.getVWSize();
        this.data[currentSize] = this.data[currentSize] || {};
        Object.assign(this.data[currentSize], {
            x1: this.x1,
            x2: this.x2,
            y1: this.y1,
            y2: this.y2,
        });
        if (currentSize === "sizeSm") {
            this.data.sizeSm.autoResized = true;
        }
        this.redraw();
    }

    playLeavingAnimation() {
        this.enterExitCtrl.playLeave();
    }

    cancelLeavingAnimation() {
        this.enterExitCtrl.cancelLeave();
    }

    clearTarget(i) {
        let target = {
            type: null,
            uuid: "auto",
            point: false,
        }
        this.data.targets[i] = target;
        Object.assign(this.targets[i], target);
    }

    setLineTarget3D(val, i = 1) {
        let lineTargetUuid = val;
        let targetPoint = null;

        if (lineTargetUuid === "auto") {
            const cx = this["targetX" + i];
            const cy = this["targetY" + i];
            const ssCenter = RenderManager.toScreenSpaceCoords(cx, cy);

            meshRaycaster.setFromCamera(ssCenter, RenderManager.activeCamera);

            let intersections = meshRaycaster.intersectObject(RenderManager.activeScene, true);
            if (intersections.length === 0) {
                const webglItsct = RenderManager.raycaster.pick(ssCenter, RenderManager.activeCamera);
                if (webglItsct.parent) {
                    webglItsct.parent.raycast(meshRaycaster, intersections, true);
                    if (intersections.length === 0 && webglItsct.parent){
                        intersections.push({
                            object: webglItsct.parent,
                            point: webglItsct.point,
                        });
                    }
                }
            }

            // snap to nearest object
            if (intersections.length === 0) {
                intersections = this.intersectWithSquare(i, 25, 0.1);
            }

            let autoTarget = null;

            for (let i = 0, len = intersections.length; i < len; i++) {
                const curObject = intersections[i].object;

                if (curObject.visible && !curObject.dontShowInHierarchyPanel &&
                    !curObject.isBackground && !curObject.isGate) {
                    autoTarget = intersections[i];
                    break;
                }
            }

            if (!autoTarget) {
                lineTargetUuid = '';
            } else {
                const inverse = new THREE.Matrix4().getInverse(autoTarget.object.matrixWorld);

                lineTargetUuid = autoTarget.object.uuid;
                targetPoint = autoTarget.point.applyMatrix4(inverse);
            }
        }

        if (lineTargetUuid) {
            this.data.targets[i].point = this.targets[i].point = targetPoint
              ? { x: targetPoint.x, y: targetPoint.y, z: targetPoint.z }
              : null;
            this.data.targets[i].type = this.targets[i].type = "3D"
            this.data.targets[i].uuid = this.targets[i].uuid = lineTargetUuid;
        } else {
            this.data.targets[i] = {
                type: null,
                uuid: "auto",
                point: false,
            }
        }

        this.render();
    }

    intersectWithSquare(linePointIndex, dimension, step) {
        let orgPoint = new THREE.Vector2(this["targetX" + linePointIndex],  this["targetY" + linePointIndex])
        let points = [];

        for (let x = orgPoint.x - dimension / 2; x <= orgPoint.x + dimension / 2; x+=step)
            for (let y = orgPoint.y - dimension / 2; y <= orgPoint.y + dimension / 2; y+=step)
                points.push(new THREE.Vector2(x, y));

        points.sort(function(a, b) { return orgPoint.distanceTo(a) - orgPoint.distanceTo(b); });

        for (let i = 0; i < points.length; i++) {
            const point = points[i];
            const ssCenter = RenderManager.toScreenSpaceCoords(point.x, point.y);
            meshRaycaster.setFromCamera(ssCenter, RenderManager.activeCamera);

            const intersections = meshRaycaster.intersectObject(RenderManager.activeScene, true);
            if (intersections.length === 0) {
                const webglItsct = RenderManager.raycaster.pick(ssCenter, RenderManager.activeCamera);

                if (!webglItsct.parent) continue;

                webglItsct.parent.raycast(meshRaycaster, intersections, true);

                if (intersections.length === 0 && webglItsct.parent) {
                    intersections.push({
                        object: webglItsct.parent,
                        point: webglItsct.point,
                    });
                }

                if (intersections.length > 0) return intersections;
            } else {
                return intersections;
            }
        }
        return [];
    }

    setLineTarget2D(uuid, i=0) {
        if (uuid) {
            let target = {
                type: "2D",
                uuid: uuid,
                point: null,
            }
            this.data.targets[i] = target;
            Object.assign(this.targets[i], target);
        }
    }

    setLineOrigin(i) {
        let size = RenderManager.getRenderViewportSize();
          let w = size.width,
          h = size.height;
        if (!Q3.editMode && this.inLetterbox) {
            const pr = RenderManager.pixelRatio;
            w = Q3.letterbox.offsetWidth;
            h = Q3.letterbox.offsetHeight;
        }
        let targetInd = 2 - i%2;
        const x = this[`x${targetInd}`] * w, y = this[`y${targetInd}`] * h;
        const pr = RenderManager.pixelRatio;

        let offsetX = 0, offsetY = 0, width = w, height = h;
        let obj2D = this.targets[i].obj2D
        if (!Q3.editMode && obj2D.inLetterbox) {
            width = Q3.letterbox.offsetWidth;
            height = Q3.letterbox.offsetHeight;
            if (!this.inLetterbox) {
                offsetX = Q3.letterbox.offsetLeft;
                offsetY = Q3.letterbox.offsetTop;
            }
        } else {
            width = w / pr;
            height = h / pr;
        }

        let bounds;
        if (Q3.editMode) {
            bounds = obj2D.getRenderPositionAndSize(width, height);
        } else {
            bounds = obj2D.domElement.getBoundingClientRect();
            bounds.x = bounds.x - Q3.letterbox.offsetLeft;
            bounds.y = bounds.y - Q3.letterbox.offsetTop;
            bounds.w = bounds.width;
            bounds.h = bounds.height;
        }

        const halfWidth = bounds.w * 0.5, halfHeight = bounds.h * 0.5;
        const origin = g_origin.set(x - offsetX, y - offsetY);

        const closestPoint = [
            g_bounds[0].set(bounds.x + halfWidth, bounds.y),
            g_bounds[1].set(bounds.x + bounds.w, bounds.y + halfHeight),
            g_bounds[2].set(bounds.x + halfWidth, bounds.y + bounds.h),
            g_bounds[3].set(bounds.x, bounds.y + halfHeight)
        ].reduce((accum, point) => {

            const dist = origin.distanceToSquared(point);

            if (dist < accum.dist) {
                accum.dist = dist;
                accum.point = point;
            }

            return accum;
        }, {
            point: null,
            dist: Infinity
        }).point;

        if (closestPoint) {
            this[`x${i+1}`] = (closestPoint.x + offsetX) / w;
            this[`y${i+1}`] = (closestPoint.y + offsetY) / h;
        }
    }

    addArrowMarker() {
        this.endpointMarker = {
            line: new EndpointMarker('arrow-' + this.uuid, 8, 4),
            outline: new EndpointMarker('arrow-outline-' + this.uuid, 8, 4 / 1.5),
        };
        this.parentLayer.defs.appendChild(this.endpointMarker.line.marker);
        this.parentLayer.defs.appendChild(this.endpointMarker.outline.marker);
    }

    removeArrowMarker() {
        this.parentLayer.defs.removeChild(this.endpointMarker.line.marker);
        this.parentLayer.defs.removeChild(this.endpointMarker.outline.marker);
        delete this.endpointMarker;
    }

    setArrowMarker(type) {
        this.endpointMarker.line.setType(type);
        this.endpointMarker.outline.setType(type);
    }

    setLineEndMarkers(start, end) {
        let markers = this.endpointMarker;
        setLineEndpointMarkers(this.lineElement, start ? markers.line : null, end ? markers.line : null);
        setLineEndpointMarkers(this.outlineElement, start ? markers.outline : null, end ? markers.outline : null);
    }

    updateArrow() {
        const { LINE, DOUBLE_ARROWS, BRACKET } = EndpointMarkers;
        let arrow = this.arrow;

        if (arrow !== LINE) {
            if (!this.endpointMarker) {
                this.addArrowMarker();
            }

            this.setArrowMarker(arrow === BRACKET ? 'bracket' : 'arrow');
        } else {
            if (this.endpointMarker) {
                this.removeArrowMarker();
            }
        }

        this.setLineEndMarkers(arrow === DOUBLE_ARROWS || arrow === BRACKET, arrow !== LINE);
    }

    removeArrow() {
        this.setLineEndMarkers(false, false);
        this.removeArrowMarker();
    }

    get targetX1() {
        const size = RenderManager.getSize();
        const width = size.width;

        return this.x2 * width;
    }

    get targetY1() {
        const size = RenderManager.getSize();
        const height = (size.height);

        return this.y2 * height;
    }

    get targetX0() {
        const size = RenderManager.getSize();
        const width = size.width;

        return this.x1 * width;
    }

    get targetY0() {
        const size = RenderManager.getSize();
        const height = (size.height);

        return this.y1 * height;
    }

    get targets() {
        return this._targets;
    }

    redraw() {
        this.render();
    }

    render(width, height) {
        // let width, height;
        if (Utils.queryParams.has("disable_2d") || Utils.queryParams.has("disable_line2d")) return;
        if (this.isDragging) return;

        // const size = RenderManager.getSize();
        const size = RenderManager.getRenderViewportSize();

        if (!width) { width = size.width; }
        if (!height) { height = size.height; }
        if (!Q3.editMode && this.inLetterbox) {
            width = Q3.letterbox.offsetWidth ;
            height = Q3.letterbox.offsetHeight;
        }
        /* ARROW */
        this.updateArrow();

        if (this.endpointMarker) {
            this.endpointMarker.line.setColor(true, this.color);
            this.endpointMarker.outline.setColor(false, this.outlineColor);
        }
        this.lineElement.setAttribute('stroke', this.color);
        this.outlineElement.setAttribute('stroke', this.outlineColor);
        /* DYNAMIC STROKE-WIDTH (because of zooming in editor) */

        this.lineElement.setAttribute("stroke-width", this.thickness * width / 1000);
        this.hiddenPath.setAttribute("stroke-width", (this.thickness * 1.5) * width / 1000 + 7);
        this.outlineElement.setAttribute("stroke-width", (this.thickness * 1.5) * width / 1000);

        if (this.lineType === "dashed") {
            this.lineElement.setAttribute("stroke-dasharray", `${this.thickness * width / 1000 * 2},${this.thickness * width / 1000 * 2}`);
            this.outlineElement.removeAttribute("stroke")
        }
        else {
            this.lineElement.removeAttribute("stroke-dasharray");
            this.outlineElement.setAttribute('stroke', this.outlineColor);
        }

        if (this.endpointMarker) {
            this.endpointMarker.outline.setStrokeWidth(1 / 3);
        }

        /* LEFT TIP */
        let x1, x2, y1, y2;

        if (this.targets[0].obj2D) { this.setLineOrigin(0); }
        if (this.targets[1].obj2D) { this.setLineOrigin(1); }

        x1 = size.x + this.x1 * width;
        y1 = size.y + this.y1 * height;

        /* RIGHT TIP */
        //attach/detach
        //positionning
        const set3DPositions = (index) => {
            let x = this[`x${index+1}`] * width,
            y = this[`y${index+1}`] * height;
            let target = this.targets[index];
            if (target.obj3D) {
                this[`vector${index}`].copy(target.point || new THREE.Vector3())
                  .applyMatrix4(target.obj3D.matrixWorld)
                  .project(RenderManager.activeCamera);

                const out = RenderManager.toScreenCoords(this[`vector${index}`].x, this[`vector${index}`].y);
                const state = RenderManager.getRenderViewportSize();

                x = out.x;
                y = out.y;

                this[`x${index+1}`] = x / state.width;
                this[`y${index+1}`] = y / state.height;
            }
            return [x, y];
        }
        [x2, y2] = set3DPositions(1);
        [x1, y1] = set3DPositions(0);

        if (Q3.selected === this) { fire("updateResizer"); }
        if (!isFinite(x1) || !isFinite(y1)) return;
        if (!isFinite(x2) || !isFinite(y2)) return;

        this._setPoints(x1, y1, x2, y2, this.thickness * width / 1000);
    }

    _setPoints(x1, y1, x2, y2, lineWidth) {
        const { SINGLE_ARROW, DOUBLE_ARROWS } = EndpointMarkers;

        const pos1 = g_pos1.set(x1, y1);
        const pos2 = g_pos2.set(x2, y2);
        const mid = g_mid.addVectors(pos1, pos2).multiplyScalar(0.5);

        const dir = g_dir.subVectors(pos2, pos1).normalize();
        const lineOffset = lineWidth * 0.5 + 3, arrowOffset = lineWidth * 3 + 3;

        const offset1 = this.arrow === DOUBLE_ARROWS ? arrowOffset : lineOffset;
        const offset2 = this.arrow === SINGLE_ARROW || this.arrow === DOUBLE_ARROWS ? arrowOffset : lineOffset;

        const off1 = g_off1.copy(dir).multiplyScalar(offset1);
        const off2 = g_off2.copy(dir).multiplyScalar(offset2);

        pos1.add(off1);
        pos2.sub(off2);

        pos1.sub(mid);
        pos2.sub(mid);

        const linePath = 'M' + pos1.x + ',' + pos1.y + ' L' + pos2.x + ',' + pos2.y;

        this.domElement.style.transform = 'translate(' + mid.x + 'px, ' + mid.y + 'px)';
        this.lineElement.setAttribute('d', linePath);
        this.outlineElement.setAttribute('d', linePath);
        this.hiddenPath.setAttribute('d', linePath);
    }

    delete() {
        if (this.arrowMarker) this.removeArrow();

        this.parentLayer.domElement.removeChild(this.domElement);
        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        this.parentLayer.objects.forEach((o, i) => o.index = i);
        this.type2dAnimation = [];
        delete this.domElement;
    }

    clone() {
        const line = new Line({
            targetUuid: this.targetUuid,
            color: this.color,
            thickness: this.thickness,
            x1: this.x1 + 0.01,
            x2: this.x2 + 0.01,
            y1: this.y1 + 0.01,
            y2: this.y2 + 0.01,
            arrow: this.arrow
        },
            this.parentLayer,
            THREE.Math.generateUUID()
        );

        Q3.data.layers.layers2D[line.parentLayer.uuid].objects[line.uuid] = {
            type: 'line',
            targetUuid: line.targetUuid,
            color: line.color,
            thickness: line.thickness,
            x1: line.x1 + 0.01,
            x2: line.x2 + 0.01,
            y1: line.y1 + 0.01,
            y2: line.y2 + 0.01,
            arrow: line.arrow
        };

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 0, line);
        this.parentLayer.objects.forEach((o, i) => o.index = i);

        return line;
    }
}

class EndpointMarker {
    constructor(id, area, size) {
        this.id = id;

        this.marker = document.createElementNS(Base2DElement.Namespace, 'marker');
        this.marker.setAttribute('id', id);

        this.path = document.createElementNS(Base2DElement.Namespace, 'path');
        this.marker.appendChild(this.path);

        this.area = area;
        this.size = size;

        this.marker.setAttribute('markerWidth', area);
        this.marker.setAttribute('markerHeight', area);
        this.marker.setAttribute('refX', area / 2 - size / 4);
        this.marker.setAttribute('refY', area / 2);
    }

    setType(type) {
        if (type === this.type) return;
        this.type = type;

        const t = this.size;

        let orient;
        let path;

        switch (type) {
            case 'arrow': {
                orient = 'auto-start-reverse';
                path = [[-t / 2, -t / 2], [-t / 2, t / 2], [t / 2, 0]];
                break;
            }

            case 'bracket':
                orient = 'auto';
                path = [[-t * 3 / 8, -t / 8], [-t * 3 / 8, t / 2], [-t / 8, t / 2], [-t / 8, -t / 8]];
                break;
        }

        let offset = this.area * 0.5
        for (let point of path) {
            point[0] += offset;
            point[1] += offset;
        }
        path = 'M' + path.map(point => point.join(',')).join(' L') + ' Z';

        this.marker.setAttribute('orient', orient);
        this.path.setAttribute('d', path);
    }

    setColor(fill, color) {
        if (fill === this.fill && color === this.color) return;
        this.fill = fill;
        this.color = color;

        this.path.setAttribute(fill ? 'stroke' : 'fill', 'none');
        this.path.setAttribute(fill ? 'fill' : 'stroke', color);
    }

    setStrokeWidth(width) {
        if (width === this.strokeWidth) return;
        this.strokeWidth = width;

        this.path.setAttribute('stroke-width', width);
    }
};

function getEndpointMarkerValue(marker) {
    if (marker) {
        return `url(#${marker.id})`;
    } else {
        return '';
    }
}

function setLineEndpointMarkers(line, start, end) {
    line.setAttribute('marker-start', getEndpointMarkerValue(start));
    line.setAttribute('marker-end', getEndpointMarkerValue(end));
}

export default Line;
export { Line, EndpointMarkers };
